/* Command bar — sim time is the most-consulted element in the interface and never scrolls away. */
import {
  Box,
  Command,
  Layers,
  Map as MapIcon,
  Pause,
  Play,
  RotateCcw,
  Shield,
} from 'lucide-react'
import { useDeck } from '../state/store'
import { simClock } from '../lib/format'
import { BasemapMenu } from './BasemapMenu'
import { OrbisMark } from './OrbisMark'

const COMPRESSIONS = [1, 5, 15, 60, 300]

export function CommandBar() {
  // Disabled when the Gateway says this role may not control the clock. The server enforces it
  // regardless; disabling here stops an operator repeatedly pressing a button that will 403.
  const mayControlTime = useDeck((s) => s.me?.mayControlTime ?? true)
  // Run header from the Gateway. Empty until connected, and the title says so rather than
  // showing a scenario name for a run that is not happening.
  const scenarioName = useDeck((s) => s.scenarioName)
  const classification = useDeck((s) => s.classification)
  const epochIso = useDeck((s) => s.epochIso)
  const running = useDeck((s) => s.running)
  const toggleRunning = useDeck((s) => s.toggleRunning)
  const resetRun = useDeck((s) => s.resetRun)
  const compression = useDeck((s) => s.compression)
  const setCompression = useDeck((s) => s.setCompression)
  const elapsedSec = useDeck((s) => s.elapsedSec)
  const viewMode = useDeck((s) => s.viewMode)
  const setViewMode = useDeck((s) => s.setViewMode)
  const layersOpen = useDeck((s) => s.layersOpen)
  const toggleLayersPanel = useDeck((s) => s.toggleLayersPanel)
  const setPaletteOpen = useDeck((s) => s.setPaletteOpen)
  const standard = useDeck((s) => s.symbolStandard)
  const setStandard = useDeck((s) => s.setSymbolStandard)

  const { date, time } = simClock(epochIso, elapsedSec)

  return (
    <header
      className="flex h-11 shrink-0 items-center gap-3 border-b px-3"
      style={{ background: 'var(--color-surface-1)', borderColor: 'var(--color-line-2)' }}
    >
      {/* Identity — the real emblem, not a stand-in icon.
          No tinted square behind it any more: the mark is a ring of orbits with its own gradient,
          and boxing it in an accent chip both dulls it and makes it read as a button. */}
      <div className="flex items-center gap-2">
        <OrbisMark size={20} />
        <span
          className="font-semibold tracking-[0.14em]"
          style={{ fontSize: 12, color: 'var(--color-text-1)' }}
        >
          ORBIS
        </span>
      </div>

      <div className="h-5 w-px" style={{ background: 'var(--color-line-2)' }} />

      {/* Scenario + classification */}
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate" style={{ fontSize: 12, color: 'var(--color-text-2)' }}>
          {scenarioName || 'Not connected'}
        </span>
        <span
          className="label shrink-0 rounded-sm px-1.5 py-px"
          style={{
            fontSize: 9,
            color: 'var(--color-warn)',
            background: 'rgba(229,169,75,0.12)',
            border: '1px solid rgba(229,169,75,0.28)',
          }}
        >
          {classification || 'UNCLASSIFIED'}
        </span>
      </div>

      {/* Sim clock — always Zulu, always dated */}
      <div className="ml-auto flex items-center gap-2">
        <div className="flex flex-col items-end leading-none">
          <span className="num" style={{ fontSize: 15, color: 'var(--color-text-1)' }}>
            {time}
          </span>
          <span className="num" style={{ fontSize: 9, color: 'var(--color-text-3)' }}>
            {date}
          </span>
        </div>

        <div className="h-5 w-px" style={{ background: 'var(--color-line-2)' }} />

        {/* Run / pause.

            When the clock is not this role's to control, the reason is shown BESIDE the button as
            well as in its title. A tooltip on a disabled control is the wrong place for the only
            explanation: it needs a hover, so it is invisible on touch, and "the play button is
            greyed out" is exactly the report it produces. */}
        <button
          className="ctl"
          onClick={toggleRunning}
          disabled={!mayControlTime}
          aria-label={running ? 'Pause simulation' : 'Resume simulation'}
          title={
            mayControlTime
              ? running
                ? 'Pause  (Space)'
                : 'Resume  (Space)'
              : 'The clock is EXCON-controlled — one participant pausing stops the exercise for everyone'
          }
          data-active={running}
        >
          {running ? <Pause size={14} /> : <Play size={14} />}
        </button>

        {/* Reset — back to tick zero.

            Confirmed, and deliberately so. It sits next to Play, it is one mis-click away, and on
            a shared exercise it rewinds the run for EVERY connected participant, not just the
            person who pressed it. An undo would be better; the kernel has checkpoints and a rewind
            endpoint, so that is a real option rather than a wish — but a confirm is the honest
            interim, where silently discarding someone else's hour is not. */}
        <button
          className="ctl"
          onClick={() => {
            if (window.confirm('Restart the scenario from the beginning?\n\nThis rewinds the run for everyone connected and clears plotted courses.')) {
              resetRun()
            }
          }}
          disabled={!mayControlTime}
          aria-label="Restart the scenario from the beginning"
          title={
            mayControlTime
              ? 'Restart from the beginning'
              : 'The clock is EXCON-controlled — restarting rewinds the run for everyone'
          }
        >
          <RotateCcw size={14} />
        </button>

        {!mayControlTime && (
          <span
            style={{ fontSize: 9, letterSpacing: '0.06em', color: 'var(--color-text-3)' }}
            className="num uppercase"
          >
            EXCON clock
          </span>
        )}

        {/* Time compression */}
        <div
          className="flex items-center gap-px rounded p-px"
          style={{ background: 'var(--color-surface-0)' }}
          role="group"
          aria-label="Time compression"
        >
          {COMPRESSIONS.map((c) => (
            <button
              key={c}
              className="ctl num"
              style={{ minWidth: 32, fontSize: 11, minHeight: 22 }}
              data-active={compression === c}
              onClick={() => setCompression(c)}
              disabled={!mayControlTime}
              aria-label={`Time compression ${c} times`}
              aria-pressed={compression === c}
              title={
                c >= 60
                  ? `×${c} — reduces sub-tick integration; see fidelity settings`
                  : `×${c} — full fidelity`
              }
            >
              ×{c}
            </button>
          ))}
        </div>

        <div className="h-5 w-px" style={{ background: 'var(--color-line-2)' }} />

        {/* 2D / 3D — preserves selection, focus, time */}
        <div
          className="flex items-center gap-px rounded p-px"
          style={{ background: 'var(--color-surface-0)' }}
          role="group"
          aria-label="View mode"
        >
          <button
            className="ctl"
            style={{ minHeight: 22 }}
            data-active={viewMode === '2d'}
            onClick={() => setViewMode('2d')}
            aria-label="2D tactical view"
            aria-pressed={viewMode === '2d'}
            title="2D tactical view  (2)"
          >
            <MapIcon size={13} />
            <span style={{ fontSize: 11 }}>2D</span>
          </button>
          <button
            className="ctl"
            style={{ minHeight: 22 }}
            data-active={viewMode === '3d'}
            onClick={() => setViewMode('3d')}
            aria-label="3D globe view"
            aria-pressed={viewMode === '3d'}
            title="3D globe view  (3)"
          >
            <Box size={13} />
            <span style={{ fontSize: 11 }}>3D</span>
          </button>
        </div>

        {/* Basemap — top-level, discoverable */}
        <BasemapMenu />

        {/* Symbology standard */}
        <button
          className="ctl"
          onClick={() => setStandard(standard === '2525E' ? 'APP6E' : '2525E')}
          aria-label={`Symbology standard: ${standard}. Click to switch.`}
          title="Switch symbology standard — both render from the same stored SIDC"
        >
          <Shield size={13} />
          <span className="num" style={{ fontSize: 10.5 }}>
            {standard === '2525E' ? '2525E' : 'APP-6E'}
          </span>
        </button>

        <button
          className="ctl"
          data-active={layersOpen}
          onClick={toggleLayersPanel}
          aria-label="Toggle layers panel"
          title="Layers  (L)"
        >
          <Layers size={14} />
        </button>

        <button
          className="ctl"
          onClick={() => setPaletteOpen(true)}
          aria-label="Open command palette"
          title="Command palette  (⌘K)"
        >
          <Command size={14} />
        </button>
      </div>
    </header>
  )
}
